<template>
  <!-- Insert a timer -->
  <q-dialog v-model="modalInsert" v-if="!isLocked">
    <q-card style="min-width: 280px" class="q-pa-md">
      <form-parent-insert :insert-data="parentTimer" />
    </q-card>
  </q-dialog>
  <!-- Timer item -->
  <q-timeline-entry
    ref="el"
    :title="text ? `${elapsedTime} ${labelActivity(parentTimer.type)}` : undefined"
    :color="color"
    :icon="icon"
    :data-unknown="parentTimer.type === 'unknown' ? index : false"
    :data-known="parentTimer.type !== 'unknown' ? index : false"
    :render-index="index"
  >
    <template v-if="text" #subtitle>
      <span @click="openModalEdit()">
        {{ subtitle }}
      </span>
      <!-- Edit existing activity -->
      <q-dialog v-model="modalEdit" v-if="parentTimer.editable" ref="editTimerRef">
        <q-card class="q-pa-md">
          <!-- Header -->
          <form-header :type="activityType" />
          <!-- Form content -->
          <form-travel
            v-if="parentTimer.type === 'travel'"
            edit
            insert
            :index="parentTimer.index!"
            :insert-times="{ start: parentTimer.start_time, end: parentTimer.end_time }"
            :locations="{
              departing: parentTimer.data.departing,
              departingStoreName: parentTimer.data.departingStoreName,
              departingStoreNumber: parentTimer.data.departingStoreNumber,
              destination: parentTimer.data.destination,
              destinationStoreName: parentTimer.data.destinationStoreName,
              destinationStoreNumber: parentTimer.data.destinationStoreNumber,
            }"
            :insert-odo="{
              startOdo: parentTimer.data.odometer_start,
              endOdo: parentTimer.data.odometer_end,
            }"
            @saved="editTimerRef!.hide()"
          />
          <form-parent-activity
            v-else
            :index="parentTimer.index!"
            insert
            editing
            :end-state="endState"
            :insert-times="{ start: parentTimer.start_time, end: parentTimer.end_time }"
            :insert-data="parentTimer.data"
            :activity-type="parentTimer.type"
            :timer="parentTimer"
            @close="editTimerRef!.hide()"
          />
        </q-card>
      </q-dialog>
    </template>

    <div>
      <!-- Travel Info -->
      <template v-if="parentTimer.type === 'travel'">
        <p v-if="parentTimer.data.odometer_end">
          {{ parentTimer.data.odometer_end - parentTimer.data.odometer_start }}
          {{ timelineLabel(user.distance.value) }}
        </p>
        <p>
          <template v-if="parentTimer.data.departing === 'store'">
            <a
              class="text-primary"
              @click="$router.push(`/stores/${parentTimer.data.departingStoreId}`)"
            >
              {{
                `${parentTimer.data.departingStoreName} #${parentTimer.data.departingStoreNumber}`
              }}
            </a>
          </template>
          <template v-else>
            <a class="text-primary" href="#">
              {{ formOptions(parentTimer.data.departing) }}
            </a>
          </template>
          <span class="text-black">→</span>
          <template v-if="parentTimer.data.destination === 'store'">
            <a
              class="text-primary"
              @click="$router.push(`/stores/${parentTimer.data.destinationStoreId}`)"
            >
              {{
                `${parentTimer.data.destinationStoreName} #${parentTimer.data.destinationStoreNumber}`
              }}
            </a>
          </template>
          <template v-else>
            <a class="text-primary" href="#">
              {{ formOptions(parentTimer.data.destination) }}
            </a>
          </template>
        </p>
      </template>
      <!-- Other parent (non-break) Info -->
      <template v-else>
        <p v-if="parentTimer.type === 'store'">
          <!-- Store -->
          <span
            class="text-primary"
            href="#"
            @click="$router.push(`/stores/${parentTimer.data.store_id}`)"
          >
            {{ `${parentTimer.data.store_name} #${parentTimer.data.store_number}` }}
          </span>
        </p>
        <p>
          <!-- Type -->
          <span class="text-primary" href="#">
            {{
              parentTimer.data?.subtype ? formLabel(`options.${parentTimer.data?.subtype}`) : null
            }}
          </span>
        </p>
        <p>
          <!-- Location -->
          <span class="text-primary" href="#">
            {{ parentTimer.data.location ? formOptions(parentTimer.data.location) : undefined }}
          </span>
        </p>
      </template>
    </div>

    <!-- Child Timers -->
  </q-timeline-entry>
</template>
<script lang="ts">
import { Timer, TimerTypes } from '../types/timers'
import { computed, DeepReadonly, defineComponent, nextTick, onMounted, PropType, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useFormatDate } from '../composables/formatDate'
import { useFormatTime } from '../composables/formatTime'
import { useTimers } from '../store/timers'
import { useIconMeta } from '../composables/iconMeta'
import { useNavActivity } from '../composables/activityMeta'
import FormTravel from './FormTravel.vue'
import FormParentActivity from './FormParentActivity.vue'
import { QDialog } from 'quasar'
import { useI18n } from 'vue-i18n'
import FormParentInsert from './FormParentInsert.vue'
import FormHeader from './FormHeader.vue'
import { useUserStore } from '../store/useUserStore'
import { isNullish } from '../utils'
export default defineComponent({
  components: { FormTravel, FormParentActivity, FormParentInsert, FormHeader },
  props: {
    parentTimer: {
      required: true,
      type: Object as PropType<{ index?: number } & DeepReadonly<Timer>>,
    },
    index: {
      required: true,
      type: Number,
    },
  },
  setup(props) {
    //translations
    const { t } = useI18n({ useScope: 'global' })
    const labelActivity = (key: string) => t(`activities.${key}`)
    const formLabel = (key: string) => t(`form.${key}`)
    const formOptions = (key: string) => t(`form.options.${key}`)
    const timelineLabel = (key: string) => t(`timeline.${key}`)

    const { getCurrentParentTime, isLocked } = storeToRefs(useTimers())
    const { insertNewTimer, editParentData, editParentStartEnd } = useTimers()
    const subtitle = computed(() => {
      if (props.parentTimer.end_time)
        return (
          useFormatDate(props.parentTimer.start_time).time() +
          ' - ' +
          useFormatDate(props.parentTimer.end_time).time()
        )
      else
        return useFormatDate(props.parentTimer.start_time).time() + ` - ${timelineLabel('current')}`
    })
    const elapsedTime = computed(() => {
      if (isNullish(props.parentTimer.end_time)) {
        const time = useFormatTime(getCurrentParentTime)
        return time
      } else {
        return useFormatTime(
          props.parentTimer.end_time.getTime() - props.parentTimer.start_time.getTime(),
          { format: 'h:mm', initTime: 'ms' }
        )
      }
    })
    const endState = computed(() => {
      return (
        typeof props.parentTimer.end_time !== 'undefined' && props.parentTimer.end_time !== null
      )
    })
    const { color, icon, text } = useIconMeta(props.parentTimer.type)
    const activityType = computed(() => useNavActivity().get(props.parentTimer.type))
    /*
     * Save data
     */
    const editTimerRef = ref<undefined | QDialog>()
    const timerSave = (data: {
      start: Date
      end: Date
      startOdo: number | undefined
      endOdo: number | undefined
    }) => {
      if (typeof props.parentTimer.index !== undefined) {
        if (typeof data.startOdo !== 'undefined' && typeof data.endOdo !== 'undefined') {
          editParentData(props.parentTimer.index!, {
            odometer_start: data.startOdo,
            odometer_end: data.endOdo,
          })
        }
        editParentStartEnd({ start_time: data.start, end_time: data.end }, props.parentTimer.index!)

        editTimerRef.value?.hide()
      }
    }

    const option = ref<TimerTypes>('break')
    const options: TimerTypes[] = [
      'break',
      'travel',
      'communication',
      'material',
      'coupon',
      'training',
      'other',
    ]
    /**
     * For creating new timeline activities
     * @param data {start and end times as dates}
     */
    const newTimerSave = (data: { start_time: Date; end_time: Date }) => {
      void insertNewTimer({
        start_time: data.start_time,
        end_time: data.end_time,
        type: option.value as Timer['type'],
      })
    }

    /*
     * Capturing unknown icon press
     */
    //element to target
    const el = ref()
    const modalInsert = ref(false)
    const openModalInsert = () => {
      if (props.parentTimer.editable) {
        modalInsert.value = true
      }
    }
    const modalEdit = ref(false)
    const openModalEdit = () => {
      if (!props.parentTimer.end_time) {
        return void 0
      }
      if (props.parentTimer.editable) {
        modalEdit.value = true
      }
    }
    const handleClick = () => {
      if (props.parentTimer.type === 'unknown') {
        openModalInsert()
      } else {
        openModalEdit()
      }
    }

    onMounted(async () => {
      await nextTick()
      if (el.value) {
        el.value.$el.children[1].addEventListener('click', handleClick)
      }
    })

    const user = storeToRefs(useUserStore())

    return {
      formLabel,
      formOptions,
      labelActivity,
      timelineLabel,
      subtitle,
      elapsedTime,
      color,
      icon,
      text,
      timerSave,
      option,
      options,
      editTimerRef,
      newTimerSave,
      activityType,
      endState,
      isLocked,
      openModalEdit,
      //unknown logic
      el,
      modalInsert,
      modalEdit,
      user,
    }
  },
})
</script>
